import { Article } from '@wix/answers-api';
import { IconButton, Text } from '@wix/design-system';
import { X } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import Image from 'next/image';
import React, { FC } from 'react';

import type { SiteMetaData } from '../../../../../../../types';
import { useArticleUnifiedBi } from '../../../../../../../utils/article-unified-bi';
import { useStore } from '../../../../../../../zustand/stores';
import { siteThumbnail } from '../../../../../../SiteSelector/SiteSelectorList/SiteSelectorListItem';

import css from './index.module.scss';

export type SitePickerSiteListItemProps = {
  article: Article;
  site: SiteMetaData;
  modalSessionId: string;
  onClose: () => void;
  showDivider: boolean;
};

export const SitePickerSiteListItem: FC<SitePickerSiteListItemProps> = ({
  article,
  site,
  modalSessionId,
  onClose,
  showDivider,
}) => {
  const { selectedSite, setUserSelectedSite } = useStore(
    (state) => state.siteSelectorSlice
  );
  const { sitePickerModalClickTrack, sitePickerRemoveTrack } =
    useArticleUnifiedBi(article);
  const isSelected = selectedSite?.metaSiteId === site.metaSiteId;
  const title = site.siteProperties?.siteDisplayName ?? site.name;

  const selectAndClose = () => {
    void sitePickerModalClickTrack(
      site.metaSiteId,
      selectedSite?.metaSiteId,
      modalSessionId,
    );
    setUserSelectedSite(site);
    onClose();
  };

  const handleRemoveSelection = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    void sitePickerRemoveTrack();
    setUserSelectedSite(null);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={selectAndClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectAndClose();
          }
        }}
        className={classNames(css.siteRow, {
          [css.siteRowSelected]: isSelected,
        })}
      >
        <Image
          src={siteThumbnail(site.metaSiteId)}
          alt={site.siteProperties?.siteDisplayName ?? site.name ?? ''}
          width={72}
          height={48}
        />
        <Text ellipsis className={css.siteRowTitle}>
          {title}
        </Text>
        {isSelected ? (
          <IconButton
            size="small"
            skin="standard"
            priority="secondary"
            onClick={handleRemoveSelection}
          >
            <X />
          </IconButton>
        ) : null}
      </div>
      {showDivider ? <div className={css.divider} /> : null}
    </>
  );
};
